import { useEffect, type KeyboardEvent, type RefObject } from 'react';

const ITEMS = '[role="menuitem"], [role="menuitemradio"], [role="group"] > button';

/** The menu's focusable rows, in reading order. */
function rows(menu: HTMLElement | null): HTMLElement[] {
  return [...(menu?.querySelectorAll<HTMLElement>(ITEMS) ?? [])];
}

/**
 * Focus inside the profile menu: opened from the keyboard, the first row takes focus; Tab and the
 * arrow keys cycle within the menu; Home and End jump; closing hands focus back to the mark.
 */
export function useMenuFocus(
  menu: RefObject<HTMLElement | null>,
  trigger: RefObject<HTMLElement | null>,
  open: boolean,
  fromKeyboard: boolean,
): { onKeyDown: (event: KeyboardEvent<HTMLElement>) => void } {
  useEffect(() => {
    if (!open) return;
    if (fromKeyboard) rows(menu.current)[0]?.focus();
    const button = trigger.current;
    const panel = menu.current;
    let inside = fromKeyboard;
    const entered = () => {
      inside = true;
    };
    panel?.addEventListener('focusin', entered);
    return () => {
      panel?.removeEventListener('focusin', entered);
      // The menu is gone; if focus went with it, the mark takes it back.
      if (inside) button?.focus({ preventScroll: true });
    };
  }, [menu, trigger, open, fromKeyboard]);

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    const items = rows(menu.current);
    if (!items.length) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next = nextIndex(event, index, items.length);
    if (next === null) return;
    event.preventDefault();
    items[next]?.focus();
  };
  return { onKeyDown };
}

/** Where a key moves focus among `count` rows from `index`; null when the key is not ours. */
function nextIndex(event: KeyboardEvent<HTMLElement>, index: number, count: number): number | null {
  const forward = (index + 1) % count;
  const back = (index - 1 + count) % count;
  switch (event.key) {
    case 'ArrowDown':
      return forward;
    case 'ArrowUp':
      return back;
    case 'Tab':
      return event.shiftKey ? back : forward;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}
